import React, { useState, useEffect, useRef, lazy, Suspense } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import {
  Search, MapPin, Building2, ArrowRight,
  ChevronRight, Loader2, SlidersHorizontal, X, UserCircle2
} from "lucide-react";
import JobCard from "../components/JobCard";
import { useAuth, getAuthHeaders } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";
import { useCountry } from "../context/CountryContext";
import { CountrySearchField } from "../components/CountrySearchField";
import { industryLabel } from "../utils/industryLabel";
import MAP_CONFIG from "../config/map-config";
import { useSplashHidden } from "../hooks/useSplashHidden";
import { usePWAStandalone } from "../hooks/usePWAStandalone";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerDescription } from "../components/ui/drawer";
import { JOB_CATEGORIES, JOB_TYPES, EXPERIENCE_LEVELS, jobTypeLabel, experienceLabel, categoryLabel } from "../config/jobTaxonomy";

// Lazy-load map to avoid SSR issues + improve initial load
const MapView = lazy(() => import("../components/MapView"));
const MapViewMapbox = lazy(() => import("../components/MapViewMapbox"));
const ActiveMapView = process.env.REACT_APP_MAPBOX_TOKEN ? MapViewMapbox : MapView;

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ── Filtros secundarios (dentro del dropdown "Filtros") ────────────────── */
const SECONDARY_FILTERS = [
  { key: "remote",   labelKey: "hero.filterRemote",       icon: "💻", params: "work_mode=remote"          },
  { key: "parttime", labelKey: "hero.filterPartTime",     icon: "🕒", params: "job_type=part-time"        },
  { key: "noexp",    labelKey: "hero.filterNoExperience", icon: "✨", params: "experience_level=entry"    },
];

/* ── Category chips (secondary, inside "Filtros" dropdown) — labels come from
   the shared `categories` translation namespace so they follow ES/EN/PT. ── */
const CAT_CHIPS = [
  { key: "Technology" }, { key: "Hospitality" }, { key: "Healthcare" }, { key: "Finance" },
  { key: "Marketing" }, { key: "Logistics" }, { key: "Education" }, { key: "Design" },
];

// Same work-mode values JobsPage already uses for its own filters — kept
// local here too since it's just 3 fixed values, not worth centralizing.
const WORK_MODES_RAW = ["remote", "hybrid", "onsite"];
/* Mobile bottom-sheet categories reuse the full shared taxonomy (JobsPage's
   own filter source of truth) instead of the 8-item CAT_CHIPS shortlist —
   "Ver todas" reveals the rest. */
const SHEET_CATEGORY_VALUES = JOB_CATEGORIES;

/* ── Main Component ──────────────────────────────────────────────────────── */
export default function HomePage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useLanguage();
  const { country, setCountry } = useCountry();

  const [keyword, setKeyword]         = useState("");
  const [locationFilter, setLocationFilter] = useState({
    countryCode: country, admin1Code: null, admin1Name: null, admin2Code: null, admin2Name: null, city: "",
  });
  const [recentJobs, setRecentJobs]   = useState([]);
  const [topCompanies, setTopCompanies] = useState([]);
  const [mapJobs, setMapJobs]         = useState([]);
  const [loadingJobs, setLoadingJobs] = useState(true);
  const [loadingMap, setLoadingMap]   = useState(true);
  const [userLocation, setUserLocation] = useState(null);
  const [geoLoading, setGeoLoading]   = useState(false);
  // Coordinates the map should flyTo — driven either by the silent "center near
  // the user" geolocation below, or by the CountrySearchField selector
  // (país/región/ciudad). Purely visual: never filters the job list on its own.
  const [mapFlyTo, setMapFlyTo] = useState(null);
  const [activeQuick, setActiveQuick] = useState(null);
  const [selectedMapJob, setSelectedMapJob] = useState(null);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [sheetFilters, setSheetFilters] = useState({ work_mode: "", job_type: "", experience_level: "", category: "" });
  const [showAllCategories, setShowAllCategories] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);
  const [mobileRecommended, setMobileRecommended] = useState([]);
  const [cvStatus, setCvStatus] = useState(null);
  const filtersRef = useRef(null);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 1024);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Lets the global AIChat FAB hide itself while this bottom sheet is open,
  // so it never floats above the sheet's own controls — resets on close/unmount.
  useEffect(() => {
    window.dispatchEvent(new CustomEvent("infocity:filters-sheet", { detail: { open: mobileFiltersOpen } }));
    return () => window.dispatchEvent(new CustomEvent("infocity:filters-sheet", { detail: { open: false } }));
  }, [mobileFiltersOpen]);

  const toggleSheetFilter = (key, value) => setSheetFilters((prev) => ({ ...prev, [key]: prev[key] === value ? "" : value }));
  const clearSheetFilters = () => setSheetFilters({ work_mode: "", job_type: "", experience_level: "", category: "" });
  const applySheetFilters = () => {
    const params = new URLSearchParams();
    if (sheetFilters.work_mode) params.set("work_mode", sheetFilters.work_mode);
    if (sheetFilters.job_type) params.set("job_type", sheetFilters.job_type);
    if (sheetFilters.experience_level) params.set("experience_level", sheetFilters.experience_level);
    if (sheetFilters.category) params.set("category", sheetFilters.category);
    setMobileFiltersOpen(false);
    navigate(`/jobs?${params.toString()}`);
  };

  useEffect(() => {
    if (!filtersOpen) return;
    const onClickOutside = (e) => {
      if (filtersRef.current && !filtersRef.current.contains(e.target)) setFiltersOpen(false);
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, [filtersOpen]);

  useEffect(() => {
    const load = async () => {
      setLoadingJobs(true);
      try {
        // Recent jobs must respect the active country selection (Multi-Country
        // consistency) — otherwise Home kept showing e.g. Madrid jobs while the
        // selector showed a different country. Companies section stays global
        // per this sprint's explicit scope (jobs/map only).
        const jobsUrl = country ? `${API}/jobs?limit=6&country_code=${country}` : `${API}/jobs?limit=6`;
        const [jobsRes, companiesRes] = await Promise.all([
          axios.get(jobsUrl),
          axios.get(`${API}/companies?limit=4`),
        ]);
        setRecentJobs(jobsRes.data.jobs || []);
        setTopCompanies(companiesRes.data.companies || []);
      } catch (err) { console.error(err); }
      finally { setLoadingJobs(false); }
    };
    load();
  }, [country]);

  // Map jobs: filtered by the persisted country selection when set (Multi-Country
  // search integration) — falls back to the original Spain-bbox preview otherwise.
  useEffect(() => {
    const loadMapJobs = async () => {
      setLoadingMap(true);
      try {
        const url = country
          ? `${API}/jobs?country_code=${country}&limit=50`
          : `${API}/jobs/in-bounds?swlat=35&swlng=-10&nelat=45&nelng=5&limit=50`;
        const res = await axios.get(url);
        setMapJobs(res.data.jobs || []);
      } catch { setMapJobs([]); }
      finally { setLoadingMap(false); }
    };
    loadMapJobs();
  }, [country]);

  // Try to center the Home map near the user's real location — separate from the
  // "Cerca de mí" filter, which stays OFF until the user explicitly taps it.
  // Gated on the launch splash being fully gone (+small buffer) so the native
  // browser geolocation permission prompt NEVER appears behind/during the
  // splash — only once Home is actually visible and stable.
  // navigator.geolocation.getCurrentPosition() already does the right thing on
  // its own: shows the native browser prompt only if permission was never
  // decided, uses the cached grant silently if already allowed, and resolves
  // the error callback immediately (no prompt) if previously denied/unavailable.
  const splashHidden = useSplashHidden();
  const isPWA = usePWAStandalone();
  useEffect(() => {
    if (!navigator.geolocation || !splashHidden) return;
    const t = setTimeout(() => {
      navigator.geolocation.getCurrentPosition(
        (pos) => setMapFlyTo({ lat: pos.coords.latitude, lon: pos.coords.longitude, zoom: 12, level: "geo" }),
        () => {}, // denied / unavailable / timeout — silently keep the country fallback, no technical error shown
        { timeout: 8000 }
      );
    }, 500);
    return () => clearTimeout(t);
  }, [splashHidden]);

  // Mobile-only "Recomendados / Cerca de ti" section — personalized for logged-in
  // candidates, otherwise falls back to recent jobs (combined with nearby once geo is on).
  // Re-fetches when the active country changes so recommendations never mix countries.
  useEffect(() => {
    if (user?.role !== "candidate") return;
    const headers = getAuthHeaders();
    if (!headers.Authorization) return;
    const recsUrl = country ? `${API}/recommendations?limit=4&country_code=${country}` : `${API}/recommendations?limit=4`;
    axios.get(recsUrl, { headers })
      .then((res) => setMobileRecommended(res.data?.jobs || []))
      .catch(() => {});
    axios.get(`${API}/cv`, { headers })
      .then((res) => setCvStatus({ exists: true, score: res.data?.cv_score || 0 }))
      .catch(() => setCvStatus({ exists: false, score: 0 }));
  }, [user, country]);

  const handleLocationChange = (partial) => {
    setLocationFilter((prev) => ({ ...prev, ...partial }));
    if (Object.prototype.hasOwnProperty.call(partial, "countryCode")) {
      setCountry(partial.countryCode || null);
    }
  };

  const handleSearch = (e) => {
    e?.preventDefault();
    const params = new URLSearchParams();
    if (keyword) params.set("keyword", keyword);
    if (locationFilter.countryCode) params.set("country_code", locationFilter.countryCode);
    if (locationFilter.admin1Name) params.set("admin1_name", locationFilter.admin1Name);
    if (locationFilter.city) params.set("city", locationFilter.city);
    navigate(`/jobs?${params.toString()}`);
  };

  const requestGeo = (destParams = "") => {
    if (!navigator.geolocation) { navigate(`/jobs?geo_error=1${destParams ? "&" + destParams : ""}`); return; }
    setGeoLoading(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude, longitude } = pos.coords;
        setUserLocation([latitude, longitude]);
        setGeoLoading(false);
        navigate(`/jobs?lat=${latitude}&lng=${longitude}&near=true${destParams ? "&" + destParams : ""}`);
      },
      () => { setGeoLoading(false); navigate(`/jobs?geo_error=1${destParams ? "&" + destParams : ""}`); },
      { timeout: 8000 }
    );
  };

  const handleMapSearchArea = (bounds) => {
    if (!bounds) return;
    const sw = bounds.getSouthWest();
    const ne = bounds.getNorthEast();
    const activeCountry = locationFilter.countryCode || country || null;
    const countryParam = activeCountry ? `&country_code=${activeCountry}` : "";
    // Bounds/country travel via router state (single source of truth for the
    // Jobs map's initial view) — never re-derived/guessed on the Jobs side.
    navigate(`/jobs?map=true${countryParam}`, {
      state: {
        mapBounds: { swLat: sw.lat, swLng: sw.lng, neLat: ne.lat, neLng: ne.lng },
        country: activeCountry,
      },
    });
  };

  return (
    <div className="min-h-screen bg-white" data-testid="home-page">

      {/* ── HERO COMPACTO ── */}
      <section className="bg-white pt-8 pb-6 px-4">
        <div className="container mx-auto max-w-2xl">
          <h1 className={`font-poppins text-3xl sm:text-4xl font-bold text-[#1A1A1A] text-center leading-tight ${isPWA ? "mb-5" : "mb-2"}`}>
            {t("hero.h1a")}{" "}
            <span style={{ color: "#028CF8" }}>{t("hero.h1b")}</span>
          </h1>
          {/* PWA standalone = feels like an app, skip the explainer copy (SEO/
              landing-page role only matters on the web version) and go straight
              from the title into the search bar. */}
          {!isPWA && (
            <>
              <p data-testid="hero-subtitle" className="text-sm sm:text-base text-[#64748B] font-poppins text-center mb-5">
                {t("hero.heroSubtitle")}
              </p>
              <p data-testid="hero-purpose-line" className="text-xs sm:text-sm text-[#94A3B8] font-poppins text-center mb-5 -mt-3">
                {t("hero.purposeLine")}
              </p>
            </>
          )}

          {/* Buscador 2 campos */}
          <form onSubmit={handleSearch} data-testid="hero-search-form"
            className="bg-white rounded-2xl shadow-lg border border-[#E5E7EB] p-2">
            <div className="flex flex-col sm:flex-row gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#94A3B8] pointer-events-none" />
                <input
                  data-testid="hero-keyword-input"
                  type="text" value={keyword} onChange={(e) => setKeyword(e.target.value)}
                  placeholder={t("hero.keywordPlaceholder")}
                  className="w-full h-11 pl-9 pr-3 text-base lg:text-sm font-poppins text-[#1A1A1A] placeholder-[#94A3B8] bg-transparent focus:outline-none rounded-xl border border-[#E5E7EB] focus:border-[#028CF8] focus:ring-2 focus:ring-[#028CF8]/15 transition-all"
                />
              </div>
              <div className="hidden sm:block w-px bg-[#E5E7EB] my-1" />
              <div className="relative flex-1">
                <CountrySearchField
                  value={locationFilter}
                  onChange={handleLocationChange}
                  onLocate={setMapFlyTo}
                  testidPrefix="hero-location"
                />
              </div>
              <button type="submit" data-testid="hero-search-button"
                className="h-11 px-6 bg-[#F55FA5] hover:bg-[#D94A8C] text-white font-semibold font-poppins rounded-xl transition-all shadow-md text-base lg:text-sm flex items-center gap-2 whitespace-nowrap">
                <Search className="h-4 w-4" /> {t("hero.searchJobs")}
              </button>
            </div>
          </form>
        </div>
      </section>

      {/* ── ACCESOS RÁPIDOS — solo 4 acciones principales ── */}
      <section className="bg-white border-b border-[#E5E7EB] py-4">
        <div className="container mx-auto px-4 md:px-6 lg:px-8">
          <div className="flex flex-wrap gap-2 justify-center sm:justify-start" ref={filtersRef} style={{ position: "relative" }}>
            <button
              onClick={() => { setActiveQuick("near"); requestGeo(); }}
              data-testid="quick-chip-near"
              disabled={geoLoading}
              style={{
                display: "inline-flex", alignItems: "center", gap: "6px",
                padding: "8px 16px", borderRadius: "999px", border: "1.5px solid",
                fontSize: "14px", fontWeight: 600, fontFamily: "Poppins, sans-serif",
                whiteSpace: "nowrap", cursor: "pointer", transition: "all 0.15s",
                borderColor: activeQuick === "near" ? "#028CF8" : "#E5E7EB",
                backgroundColor: activeQuick === "near" ? "#028CF8" : "white",
                color: activeQuick === "near" ? "white" : "#64748B",
              }}>
              {geoLoading ? <Loader2 style={{ width: 14, height: 14, animation: "spin 1s linear infinite" }} /> : <MapPin style={{ width: 14, height: 14 }} />}
              {geoLoading ? t("hero.locating") : t("hero.useMyLocation")}
            </button>

            <button
              onClick={() => { setActiveQuick("today"); navigate("/jobs?starts_today=true"); }}
              data-testid="quick-chip-today"
              style={{
                display: "inline-flex", alignItems: "center", gap: "6px",
                padding: "8px 16px", borderRadius: "999px", border: "1.5px solid",
                fontSize: "14px", fontWeight: 600, fontFamily: "Poppins, sans-serif",
                whiteSpace: "nowrap", cursor: "pointer", transition: "all 0.15s",
                borderColor: activeQuick === "today" ? "#028CF8" : "#E5E7EB",
                backgroundColor: activeQuick === "today" ? "#028CF8" : "white",
                color: activeQuick === "today" ? "white" : "#64748B",
              }}>
              🕐 {t("hero.forToday")}
            </button>

            <button
              onClick={() => { setActiveQuick("urgent"); navigate("/jobs?is_urgent=true"); }}
              data-testid="quick-chip-urgent"
              style={{
                display: "inline-flex", alignItems: "center", gap: "6px",
                padding: "8px 16px", borderRadius: "999px", border: "1.5px solid",
                fontSize: "14px", fontWeight: 600, fontFamily: "Poppins, sans-serif",
                whiteSpace: "nowrap", cursor: "pointer", transition: "all 0.15s",
                borderColor: activeQuick === "urgent" ? "#028CF8" : "#E5E7EB",
                backgroundColor: activeQuick === "urgent" ? "#028CF8" : "white",
                color: activeQuick === "urgent" ? "white" : "#64748B",
              }}>
              ⚡ {t("hero.urgentOnly")}
            </button>

            <div style={{ position: "relative" }}>
              <button
                onClick={() => { if (isMobile) setMobileFiltersOpen(true); else setFiltersOpen((v) => !v); }}
                data-testid="quick-chip-filters"
                style={{
                  display: "inline-flex", alignItems: "center", gap: "6px",
                  padding: "8px 16px", borderRadius: "999px", border: "1.5px solid",
                  fontSize: "14px", fontWeight: 600, fontFamily: "Poppins, sans-serif",
                  whiteSpace: "nowrap", cursor: "pointer", transition: "all 0.15s",
                  borderColor: filtersOpen ? "#028CF8" : "#E5E7EB",
                  backgroundColor: filtersOpen ? "#028CF8" : "white",
                  color: filtersOpen ? "white" : "#64748B",
                }}>
                <SlidersHorizontal style={{ width: 14, height: 14 }} /> {t("hero.filtersBtn")}
              </button>

              {filtersOpen && (
                <div data-testid="filters-dropdown"
                  className="absolute right-0 top-[calc(100%+8px)] z-20 bg-white rounded-2xl border border-[#E5E7EB] shadow-xl p-4"
                  style={{ width: "260px", maxWidth: "calc(100vw - 32px)" }}>
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-xs font-bold uppercase tracking-wide text-[#94A3B8] font-poppins">{t("hero.filtersBtn")}</span>
                    <button onClick={() => setFiltersOpen(false)} data-testid="filters-dropdown-close" aria-label="Cerrar filtros">
                      <X className="h-4 w-4 text-[#94A3B8]" />
                    </button>
                  </div>
                  <div className="flex flex-col gap-1.5 mb-3">
                    {SECONDARY_FILTERS.map(({ key, labelKey, icon, params }) => (
                      <button key={key}
                        onClick={() => { setFiltersOpen(false); navigate(`/jobs?${params}`); }}
                        data-testid={`filter-option-${key}`}
                        className="flex items-center gap-2 text-left text-sm font-poppins text-[#334155] hover:text-[#028CF8] hover:bg-[#028CF8]/5 rounded-lg px-2.5 py-2 transition-all">
                        <span>{icon}</span> {t(labelKey)}
                      </button>
                    ))}
                  </div>
                  <div className="border-t border-[#E5E7EB] pt-3">
                    <span className="text-xs font-bold uppercase tracking-wide text-[#94A3B8] font-poppins block mb-2">{t("hero.categoriesLabel")}</span>
                    <div className="flex flex-wrap gap-1.5">
                      {CAT_CHIPS.map(({ key }) => (
                        <button key={key}
                          onClick={() => { setFiltersOpen(false); navigate(`/jobs?category=${key}`); }}
                          data-testid={`cat-chip-${key}`}
                          className="text-xs font-medium font-poppins text-[#64748B] border border-[#E5E7EB] rounded-full px-2.5 py-1 hover:border-[#028CF8] hover:text-[#028CF8] transition-all">
                          {t(`categories.${key}`)}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ── Filtros — bottom sheet mobile (reutiliza el mismo Drawer/vaul que
          JobsPage usa para su propio panel de filtros movil) ── */}
      <Drawer open={mobileFiltersOpen} onOpenChange={setMobileFiltersOpen}>
        <DrawerContent data-testid="home-filters-drawer" style={{ maxHeight: "85vh" }}>
          <DrawerHeader className="flex items-center justify-between flex-row">
            <DrawerTitle className="font-poppins">{t("hero.filtersBtn")}</DrawerTitle>
            <DrawerDescription className="sr-only">{t("hero.filtersBtn")}</DrawerDescription>
            <button onClick={() => setMobileFiltersOpen(false)} data-testid="home-filters-drawer-close" aria-label="Cerrar filtros">
              <X className="h-5 w-5 text-[#94A3B8]" />
            </button>
          </DrawerHeader>
          <div className="px-4 overflow-y-auto" style={{ maxHeight: "60vh" }}>
            <div className="space-y-5 pb-4">
              {/* Modalidad */}
              <div>
                <span className="text-xs font-bold uppercase tracking-wide text-[#94A3B8] font-poppins block mb-2">{t("jobsPage.workModeLabel")}</span>
                <div className="flex flex-wrap gap-2">
                  {WORK_MODES_RAW.map((v) => {
                    const labelKey = v === "remote" ? "jobsPage.workModeRemote" : v === "hybrid" ? "jobsPage.workModeHybrid" : "jobsPage.workModeOnsite";
                    const active = sheetFilters.work_mode === v;
                    return (
                      <button key={v} type="button" data-testid={`sheet-workmode-${v}`}
                        onClick={() => toggleSheetFilter("work_mode", v)}
                        className={`text-sm font-medium font-poppins rounded-full px-3.5 py-2 border transition-all ${active ? "bg-[#028CF8] border-[#028CF8] text-white" : "border-[#E5E7EB] text-[#64748B]"}`}>
                        {t(labelKey)}
                      </button>
                    );
                  })}
                </div>
              </div>
              {/* Jornada */}
              <div>
                <span className="text-xs font-bold uppercase tracking-wide text-[#94A3B8] font-poppins block mb-2">{t("jobsPage.jobTypeLabel")}</span>
                <div className="flex flex-wrap gap-2">
                  {JOB_TYPES.map((v) => {
                    const active = sheetFilters.job_type === v;
                    return (
                      <button key={v} type="button" data-testid={`sheet-jobtype-${v}`}
                        onClick={() => toggleSheetFilter("job_type", v)}
                        className={`text-sm font-medium font-poppins rounded-full px-3.5 py-2 border transition-all ${active ? "bg-[#028CF8] border-[#028CF8] text-white" : "border-[#E5E7EB] text-[#64748B]"}`}>
                        {jobTypeLabel(v, t)}
                      </button>
                    );
                  })}
                </div>
              </div>
              {/* Experiencia */}
              <div>
                <span className="text-xs font-bold uppercase tracking-wide text-[#94A3B8] font-poppins block mb-2">{t("jobsPage.experienceLabel")}</span>
                <div className="flex flex-wrap gap-2">
                  {EXPERIENCE_LEVELS.map((v) => {
                    const active = sheetFilters.experience_level === v;
                    return (
                      <button key={v} type="button" data-testid={`sheet-experience-${v}`}
                        onClick={() => toggleSheetFilter("experience_level", v)}
                        className={`text-sm font-medium font-poppins rounded-full px-3.5 py-2 border transition-all ${active ? "bg-[#028CF8] border-[#028CF8] text-white" : "border-[#E5E7EB] text-[#64748B]"}`}>
                        {experienceLabel(v, t)}
                      </button>
                    );
                  })}
                </div>
              </div>
              {/* Categoría */}
              <div>
                <span className="text-xs font-bold uppercase tracking-wide text-[#94A3B8] font-poppins block mb-2">{t("hero.categoriesLabel")}</span>
                <div className="flex flex-wrap gap-2">
                  {(showAllCategories ? SHEET_CATEGORY_VALUES : SHEET_CATEGORY_VALUES.slice(0, 4)).map((v) => {
                    const active = sheetFilters.category === v;
                    return (
                      <button key={v} type="button" data-testid={`sheet-category-${v}`}
                        onClick={() => toggleSheetFilter("category", v)}
                        className={`text-sm font-medium font-poppins rounded-full px-3.5 py-2 border transition-all ${active ? "bg-[#028CF8] border-[#028CF8] text-white" : "border-[#E5E7EB] text-[#64748B]"}`}>
                        {categoryLabel(v, t)}
                      </button>
                    );
                  })}
                </div>
                {SHEET_CATEGORY_VALUES.length > 4 && (
                  <button type="button" onClick={() => setShowAllCategories((v) => !v)} data-testid="sheet-categories-toggle"
                    className="text-sm font-semibold text-[#028CF8] font-poppins mt-2 hover:underline">
                    {showAllCategories ? t("jobsPage.showLess") : t("jobsPage.showAll")}
                  </button>
                )}
              </div>
            </div>
          </div>
          {/* Fixed actions */}
          <div className="flex gap-2 px-4 border-t border-[#E5E7EB] pt-3" style={{ paddingBottom: "calc(12px + env(safe-area-inset-bottom))" }}>
            <button data-testid="home-filters-clear-button" onClick={clearSheetFilters}
              className="flex-1 h-12 rounded-xl border border-[#E5E7EB] text-[#64748B] font-poppins font-semibold text-base bg-white">
              {t("jobsPage.clear")}
            </button>
            <button data-testid="home-filters-apply-button" onClick={applySheetFilters}
              className="flex-1 h-12 rounded-xl bg-[#028CF8] text-white font-poppins font-bold text-base">
              {t("hero.viewJobsBtn")}
            </button>
          </div>
        </DrawerContent>
      </Drawer>


      {/* ── RECOMENDADOS PARA TI / EMPLEOS RECIENTES — reutiliza /api/recommendations
          (mismo calculate_job_match_score que usa Employer para ranking). Universal:
          antes solo aparecía en mobile, ahora se muestra en todos los breakpoints,
          siempre entre el buscador y el mapa (nunca el mapa por encima). ── */}
      <section className="bg-white py-5 lg:py-10" data-testid="recommended-jobs-section">
        <div className="container mx-auto px-4 md:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-3 lg:mb-5">
            <h2 className="font-poppins text-base lg:text-2xl font-bold text-[#1A1A1A]">
              {user?.role === "candidate"
                ? t("sections.recommendedForYouShort")
                : (userLocation ? t("jobsPage.nearMeBadge") : t("sections.availableJobsLabel"))}
            </h2>
            <Link to="/jobs" data-testid="recommended-jobs-view-all" className="flex items-center gap-1 text-xs lg:text-sm font-semibold text-[#028CF8] font-poppins hover:gap-2 transition-all">
              {t("sections.featuredViewAll")} <ArrowRight className="h-3.5 w-3.5 lg:h-4 lg:w-4" />
            </Link>
          </div>
          {user?.role === "candidate" && cvStatus && !cvStatus.exists && (
            <Link to="/dashboard?tab=myprofile" data-testid="incomplete-profile-hint"
              className="flex items-center gap-2 text-xs text-[#028CF8] bg-[#EFF6FF] rounded-xl px-3.5 py-2.5 mb-3 font-poppins font-medium hover:bg-[#DBEAFE] transition-all">
              <UserCircle2 className="h-3.5 w-3.5 flex-shrink-0" /> {t("sections.completeProfileForRecs")}
            </Link>
          )}
          {(() => {
            const list = user?.role === "candidate" ? mobileRecommended : (userLocation ? mapJobs : recentJobs);
            if (loadingJobs && list.length === 0) {
              return (
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 lg:gap-4">
                  {[...Array(3)].map((_, i) => <div key={i} className="bg-[#F8FAFC] rounded-2xl border border-[#E5E7EB] animate-pulse" style={{ height: "120px" }} />)}
                </div>
              );
            }
            if (list.length === 0) {
              return <p className="text-sm text-[#94A3B8] font-poppins py-4">{t("sections.noJobsYet")}</p>;
            }
            return (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 lg:gap-4" data-testid="recommended-jobs-grid">
                {list.slice(0, 5).map((job) => <JobCard key={job.job_id} job={job} compact showMatchScore={user?.role === "candidate"} />)}
              </div>
            );
          })()}
        </div>
      </section>

      {/* ── MAPA REAL — sección de descubrimiento ── */}
      <section className="bg-[#F8FAFC] border-t border-b border-[#E5E7EB]" data-testid="map-section">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 py-8">
          <div className="mb-5">
            <p style={{ fontSize: "11px", fontWeight: 700, color: "#028CF8", textTransform: "uppercase", letterSpacing: "0.08em", fontFamily: "Poppins, sans-serif" }}>
              {t("sections.mapLabel")}
            </p>
            <h2 className="font-poppins text-xl md:text-2xl font-bold text-[#1A1A1A]">
              {t("sections.mapTitle")}
            </h2>
            <p className="text-sm text-[#64748B] font-poppins mt-1">
              {t("sections.mapSubtitle")}
            </p>
          </div>

          {/* Mapa */}
          <div className="rounded-2xl overflow-hidden border border-[#E5E7EB] shadow-sm"
            style={{ height: "500px" }}>
            <Suspense fallback={
              <div style={{ height: "500px", background: "#F8FAFC", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Loader2 style={{ width: 32, height: 32, color: "#028CF8", animation: "spin 1s linear infinite" }} />
              </div>
            }>
              {!loadingMap && (
                <ActiveMapView
                  jobs={mapJobs}
                  height="500px"
                  fallbackCenter={country ? MAP_CONFIG.countryDefaults[country]?.center : undefined}
                  fallbackZoom={country ? MAP_CONFIG.countryDefaults[country]?.zoom : undefined}
                  userLocation={userLocation}
                  flyToTarget={mapFlyTo}
                  selectedJobId={selectedMapJob?.job_id}
                  onJobSelect={setSelectedMapJob}
                  onJobSaveToggle={(jobId, isSaved) => setMapJobs((prev) => prev.map((j) => (j.job_id === jobId ? { ...j, is_saved: isSaved } : j)))}
                  showSearchArea
                  onSearchArea={handleMapSearchArea}
                />
              )}
              {loadingMap && (
                <div style={{ height: "500px", background: "#F8FAFC", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Loader2 style={{ width: 32, height: 32, color: "#028CF8", animation: "spin 1s linear infinite" }} />
                </div>
              )}
            </Suspense>
          </div>

          <div className="flex justify-center mt-4">
            <Link to="/jobs?map=true"
              data-testid="view-all-map-link"
              style={{ display: "inline-flex", alignItems: "center", gap: "6px", padding: "9px 22px", borderRadius: "999px", border: "1.5px solid #028CF8", color: "#028CF8", background: "white", fontSize: "13px", fontWeight: 600, fontFamily: "Poppins, sans-serif", textDecoration: "none", transition: "all 0.2s" }}
              className="hover:bg-[#028CF8]/5">
              {t("cta.viewFullMap")}
            </Link>
          </div>
        </div>
      </section>

      {/* ── EMPLEOS DISPONIBLES — debajo del mapa, máx. 3 en desktop (desktop-only, mobile has its own recommended section above) ── */}
      {/* ── TOP COMPANIES (desktop-only — keeps mobile home focused on the job search task) ── */}
      {topCompanies.length > 0 && (
        <section className="hidden lg:block py-10 bg-white" data-testid="top-companies-section">
          <div className="container mx-auto px-4 md:px-6 lg:px-8">
            <div className="flex items-center justify-between mb-5">
              <div>
                <p style={{ fontSize: "11px", fontWeight: 700, color: "#028CF8", textTransform: "uppercase", letterSpacing: "0.08em", fontFamily: "Poppins, sans-serif" }}>
                  {t("sections.companiesLabel")}
                </p>
                <h2 className="font-poppins text-xl md:text-2xl font-bold text-[#1A1A1A]">
                  {t("sections.companiesTitle")}
                </h2>
              </div>
              <Link to="/companies"
                className="hidden sm:flex items-center gap-1 text-sm font-semibold text-[#028CF8] font-poppins hover:gap-2 transition-all">
                {t("sections.companiesViewAll")} <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4" data-testid="top-companies-grid">
              {topCompanies.map((company) => (
                <Link key={company.company_id} to={`/companies/${company.company_id}`}
                  data-testid={`top-company-${company.company_id}`}
                  className="group bg-white rounded-2xl border border-[#E5E7EB] p-4 shadow-sm hover:shadow-lg hover:border-[#028CF8]/30 hover:-translate-y-0.5 transition-all">
                  <div className="h-10 w-10 rounded-xl bg-[#028CF8]/10 flex items-center justify-center font-poppins font-bold text-[#028CF8] text-lg mb-3 overflow-hidden">
                    {company.logo_url ? (
                      <img src={company.logo_url} alt={company.name} className="w-full h-full object-cover" />
                    ) : (
                      company.name?.[0]?.toUpperCase()
                    )}
                  </div>
                  <h3 className="font-poppins font-semibold text-[#1A1A1A] text-sm group-hover:text-[#028CF8] transition-colors truncate">{company.name}</h3>
                  <p className="text-xs text-[#64748B] mt-0.5 truncate">
                    {[industryLabel(company.industry, t), company.location].filter(Boolean).join(" · ")}
                  </p>
                  <p className="text-xs font-semibold text-[#028CF8] mt-2 flex items-center gap-1" data-testid={`company-job-count-${company.company_id}`}>
                    {company.job_count || 0} {(company.job_count || 0) === 1 ? t("sections.jobCountSingular") : t("sections.jobCountPlural")}
                    <ArrowRight className="h-3 w-3" />
                  </p>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── TU CV — personal tool, not a pitch. Real completion %, no promo copy ── */}
      {user?.role !== "employer" && (
        <section className="hidden lg:block py-8 bg-[#F8FAFC] border-t border-[#E5E7EB]">
          <div className="container mx-auto px-4 md:px-6 lg:px-8">
            <div className="max-w-2xl mx-auto rounded-2xl border border-[#E5E7EB] bg-white p-6 flex flex-col sm:flex-row items-center gap-5 shadow-sm">
              <div className="flex-1">
                <h2 className="font-poppins text-lg font-bold text-[#1A1A1A]">
                  {!cvStatus?.exists
                    ? t("homeSections.cvStateIncomplete")
                    : cvStatus.score >= 100
                      ? t("homeSections.cvTitle")
                      : `${cvStatus.score}% ${t("homeSections.cvComplete")}`}
                </h2>
              </div>
              <Link
                to={user ? "/cv-builder" : "/auth?tab=register"}
                data-testid="cv-builder-cta"
                className="flex items-center justify-center gap-1.5 h-11 px-6 rounded-xl bg-[#028CF8] hover:bg-[#0270C5] text-white font-semibold font-poppins text-sm transition-all w-full sm:w-auto"
              >
                {!cvStatus?.exists
                  ? t("homeSections.createMyCV")
                  : cvStatus.score >= 100
                    ? t("homeSections.viewEditCV")
                    : t("homeSections.continueCV")}
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* ── PARA EMPLEADORES — slim, secondary banner (never competes with job search/CV) ── */}
      <section className="hidden lg:block py-4 bg-white border-t border-[#E5E7EB]">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 flex items-center justify-center gap-3 text-center">
          <span className="text-sm text-[#94A3B8] font-poppins">{t("homeSections.hiringQuestion")}</span>
          <Link to={user?.role === "employer" ? "/company/dashboard" : "/auth?tab=register"}
            data-testid="cta-post-job"
            className="text-sm font-semibold text-[#028CF8] font-poppins hover:underline">
            {t("homeSections.postJobLink")} →
          </Link>
        </div>
      </section>

    </div>
  );
}
